import { useEffect, useState } from 'react'
import { Grid, Input, Table, Button, Spinner } from '@design-systems/canvas-ui'
import { transformRfiApprovalData } from '../../common/rfiHelper'
import {
  LOCATION_NAME,
  RFI_LAST_MODIFIED_DATE,
  RFI_TYPE_SUPPLIER,
  SORT_ASCENDING,
  SORT_DESCENDING,
  SUPPLIER_NAME,
} from '../../common/commonConstants'
import {
  exportRFIApprovalExcel,
  getRFIApprovalList,
} from '../../services/lcmService'
import Icon, {
  CloseIcon,
  DownloadIcon,
  FilterFunnelIcon,
  SearchIcon,
} from '@design-systems/react-icons'
import TableFilterModal from './TableFilterModal'
import AppliedFilterChips from './AppliedFilterChips'
import { isEmpty } from 'lodash'
import { useEnv } from '@praxis/component-runtime-env'

const EMPTY_FILTER_VALUES = {
  manufacturing_type: '',
  rfi1_status: null,
  rfi2_status: null,
  rfi3_status: null,
  supplier_types: '',
  ctv_sourcing_office: '',
  ctp_sourcing_office: '',
  ctp_psqa_office: '',
  rfi_last_modified_date: null,
}
const MAX_VISIBLE_CHIPS = 3

const DEFAULT_FILTER_KEYS = Object.keys(EMPTY_FILTER_VALUES)

const VettingSummaryTable = (props) => {
  const [vettingListData, setVettingListData] = useState([])
  const [searchText, setSearchText] = useState('')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState(null)
  const [filterData, setFilterData] = useState({})
  const [maxVisibleChips, setMaxVisibleChips] = useState(MAX_VISIBLE_CHIPS)

  const env = useEnv()
  const pageSizeOptions = env.mfelcm?.pageSize || [5, 10, 15]

  const params = {
    vettingType: '',
    pageNumber: 1,
    perPage: 15,
    sortDirectionValue: SORT_ASCENDING,
    sortByField: '',
    searchQuery: '',
    ...EMPTY_FILTER_VALUES,
  }

  const [queryParams, setQueryParams] = useState(params)

  useEffect(() => {
    if (props.vettingType) {
      setSearchText('')

      const newField =
        props.vettingType === RFI_TYPE_SUPPLIER ? SUPPLIER_NAME : LOCATION_NAME

      if (queryParams.sortByField !== newField) {
        updateQueryParams({
          ...params,
          sortByField: newField,
          vettingType: props.vettingType,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.vettingType])

  useEffect(() => {
    if (props.vettingType) {
      getRFIApprovalSummaryList(
        [queryParams.vettingType],
        queryParams.pageNumber,
        queryParams.perPage,
        queryParams.sortDirectionValue,
        queryParams.sortByField,
        queryParams.searchQuery,
        queryParams.manufacturing_type,
        queryParams.rfi1_status,
        queryParams.rfi2_status,
        queryParams.rfi3_status,
        queryParams.supplier_types,
        queryParams.ctv_sourcing_office,
        queryParams.ctp_sourcing_office,
        queryParams.ctp_psqa_office,
        queryParams.rfi_last_modified_date,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams])

  const normalizeFilterValue = (filterKey, selectedValue) => {
    if (filterKey === RFI_LAST_MODIFIED_DATE) {
      return selectedValue?.startDate || selectedValue?.endDate
        ? selectedValue
        : null
    }

    return Array.isArray(selectedValue) && selectedValue.length > 0
      ? selectedValue
      : EMPTY_FILTER_VALUES[filterKey]
  }

  const getSelectedFiltersFromQueryParams = () => {
    const filterKeys =
      Object.keys(filterData).length > 0
        ? Object.keys(filterData)
        : DEFAULT_FILTER_KEYS

    return filterKeys.reduce((selectedFilters, filterKey) => {
      const selectedValue = queryParams[filterKey]

      if (filterKey === RFI_LAST_MODIFIED_DATE) {
        if (selectedValue?.startDate || selectedValue?.endDate) {
          selectedFilters[filterKey] = selectedValue
        }

        return selectedFilters
      }

      if (Array.isArray(selectedValue) && selectedValue.length > 0) {
        selectedFilters[filterKey] = selectedValue
      }

      return selectedFilters
    }, {})
  }

  const selectedFilters = getSelectedFiltersFromQueryParams()

  const handleFilterUpdate = (newFilters = {}) => {
    setMaxVisibleChips(MAX_VISIBLE_CHIPS)
    const normalizedFilters = Object.entries(newFilters).reduce(
      (filters, [filterKey, selectedValue]) => {
        filters[filterKey] = normalizeFilterValue(filterKey, selectedValue)
        return filters
      },
      {},
    )
    updateQueryParams({
      ...EMPTY_FILTER_VALUES,
      ...normalizedFilters,
    })
  }

  const handleSort = (direction, field) => {
    const newSortDirection =
      direction === 'asc' ? SORT_ASCENDING : SORT_DESCENDING

    updateQueryParams({
      sortByField: field,
      sortDirectionValue: newSortDirection,
    })
  }

  const sortingState =
    queryParams.sortByField && queryParams.sortDirectionValue
      ? [
          {
            id: queryParams.sortByField,
            desc: queryParams.sortDirectionValue === SORT_DESCENDING,
          },
        ]
      : []

  const handleSortingChange = (updater) => {
    const nextState =
      typeof updater === 'function' ? updater(sortingState) : updater
    const sortEntry = Array.isArray(nextState) ? nextState[0] : null
    if (sortEntry) {
      handleSort(sortEntry.desc ? 'desc' : 'asc', sortEntry.id)
    }
  }

  const updateQueryParams = (updates) => {
    setQueryParams((prev) => ({
      ...prev,
      ...updates,
      pageNumber:
        updates.pageNumber !== undefined
          ? updates.pageNumber
          : updates.perPage ||
              Object.keys(updates).some(
                (key) => key !== 'pageNumber' && key !== 'sortDirection',
              )
            ? 1
            : prev.pageNumber,
    }))
  }

  /**
   * Get RFI Approval summary list
   */
  const getRFIApprovalSummaryList = async (
    vettingType,
    pageNumber,
    pageSize,
    sortDirectionValue,
    sortBy,
    searchQuery,
    manufacturingType,
    rfi1Status,
    rfi2Status,
    rfi3Status,
    supplierType,
    ctvSourcingOffice,
    ctpSourcingOffice,
    ctpPsqaOffice,
    rfiLastModifiedDate,
  ) => {
    try {
      props.setIsGetApprovalInProgress(true)
      props.setIsGetApprovalFailed(false)

      let responseApprovalList = await getRFIApprovalList(
        vettingType,
        pageNumber - 1,
        pageSize,
        sortDirectionValue,
        sortBy,
        searchQuery,
        manufacturingType,
        rfi1Status,
        rfi2Status,
        rfi3Status,
        supplierType,
        ctvSourcingOffice,
        ctpSourcingOffice,
        ctpPsqaOffice,
        rfiLastModifiedDate,
      )

      if (responseApprovalList?.approval_requests) {
        let transformedList = transformRfiApprovalData(
          responseApprovalList?.approval_requests,
          props.vettingType,
        )
        setVettingListData(transformedList)
        setTotalCount(responseApprovalList?.total_count)
      } else {
        setVettingListData([])
        setTotalCount(0)
      }

      props.setIsGetApprovalSuccess(true)
      props.setIsGetApprovalInProgress(false)
      props.setIsGetApprovalFailed(false)
    } catch (error) {
      props.setIsGetApprovalInProgress(false)
      props.setIsGetApprovalFailed(true)
    }
  }

  const handleExport = async () => {
    setExportError(null)
    setExporting(true)

    try {
      await exportRFIApprovalExcel(
        [queryParams.vettingType],
        queryParams.sortDirectionValue,
        queryParams.sortByField,
        queryParams.searchQuery,
        queryParams.manufacturing_type,
        queryParams.rfi1_status,
        queryParams.rfi2_status,
        queryParams.rfi3_status,
        queryParams.supplier_types,
        queryParams.ctv_sourcing_office,
        queryParams.ctp_sourcing_office,
        queryParams.ctp_psqa_office,
        queryParams.rfi_last_modified_date,
      )
    } catch (e) {
      setExportError(e?.message || 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  const supplierHeaderColumns = [
    { id: 'supplierId', header: 'Supplier ID', isSortable: true },
    {
      id: 'supplierName',
      header: 'Supplier Name',
      isRowHeader: true,
      isSortable: true,
    },
    {
      id: 'manufacturingType',
      header: 'Manufacturing Type',
      isSortable: true,
    },
    { id: 'departments', header: 'Departments' },
    { id: 'rfiTimeline', header: 'RFI Timelines' },
  ]

  const locationHeaderColumns = [
    { id: 'locationId', header: 'Location ID', isSortable: true },
    {
      id: 'locationName',
      header: 'Location Name',
      isRowHeader: true,
      isSortable: true,
    },
    {
      id: 'manufacturingType',
      header: 'Manufacturing Type',
      isSortable: true,
    },
    { id: 'supplierId', header: 'Supplier ID', isSortable: true },
    {
      id: 'supplierType',
      header: 'Supplier Type',
      isSortable: true,
    },
    { id: 'supplierNameLoc', header: 'Supplier Name' },
    { id: 'rfiTimeline', header: 'RFI Timelines' },
  ]

  return (
    <>
      <Grid.Container
        className="hc8-pt-4x"
        direction="column"
        justify="space-between"
      >
        <Grid.Item className="search-bar hc8-ph-6x hc8-pv-2x">
          <Grid.Item size={{ xs: 12 }} className="search-bar-container">
            <Grid.Item
              size={{ xs: 8 }}
              className="hc8-pl-4x applied-filter-chip-grid"
            >
              <AppliedFilterChips
                allSelectedFilters={selectedFilters}
                filterData={filterData}
                handleFilterUpdate={handleFilterUpdate}
                maxVisibleChips={maxVisibleChips}
                setMaxVisibleChips={setMaxVisibleChips}
              />
            </Grid.Item>

            <Grid.Item size={{ xs: 4 }} className="search-input-bar">
              <div className="search-field-wrap vetting-summary-search">
                <Icon
                  svg={SearchIcon}
                  className="color-grey search-field-icon"
                  aria-hidden
                />

                <Input.Text
                  className="search-input"
                  value={searchText}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      updateQueryParams({ searchQuery: searchText })
                    }
                  }}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={`Search ${
                    props.vettingType === RFI_TYPE_SUPPLIER
                      ? 'Supplier'
                      : 'Location'
                  }`}
                />

                {!isEmpty(searchText) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    isIconOnly
                    aria-label="Clear search"
                    className="search-clear-btn"
                    onClick={() => {
                      updateQueryParams({ searchQuery: '' })
                      setSearchText('')
                    }}
                  >
                    <Icon svg={CloseIcon} className="color-grey" />
                  </Button>
                )}
              </div>

              <Button
                size="md"
                className="hc8-mr-4x search-btn"
                variant="primary"
                onClick={() => updateQueryParams({ searchQuery: searchText })}
              >
                Search
              </Button>

              <Button
                variant="ghost"
                size="sm"
                isIconOnly
                aria-label="Open filters"
                className="vetting-filter-button"
                onClick={() => setIsFilterOpen(true)}
              >
                <Icon
                  svg={FilterFunnelIcon}
                  className={`search-icons vetting-filter-icon ${isFilterOpen ? 'color-highlight' : 'color-grey'}`}
                />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                isIconOnly
                aria-label="Export to Excel"
                data-testid="btn-export"
                onClick={handleExport}
                isDisabled={exporting}
              >
                {exporting ? (
                  <Spinner size="sm" />
                ) : (
                  <Icon svg={DownloadIcon} />
                )}
              </Button>
            </Grid.Item>
          </Grid.Item>
        </Grid.Item>

        <Grid.Item className="hc8-pv-none">
          {props.isGetApprovalInProgress && !props.isGetApprovalFailed && (
            <Grid.Item
              size={{ xs: 10 }}
              className="align-centre-centre"
              style={{
                paddingBottom: '100%',
              }}
            >
              <Spinner
                data-testid="spinner-vetting-landing"
                size="lg"
                className="align-centre-centre"
              />
            </Grid.Item>
          )}

          <Table
            key={`vetting-summary-${queryParams.perPage}`}
            name="Vetting summary"
            className={`cursor-hand table-style vetting-summary-table ${
              props.vettingType === RFI_TYPE_SUPPLIER
                ? 'vetting-summary-table--supplier'
                : 'vetting-summary-table--location'
            }`}
            data-testid="table-bpr"
            cellPadding="dense"
            height={550}
            isHeaderFixed
            isWidthScrollable
            mode="server"
            sorting={{
              sortingState,
              onSortingChange: handleSortingChange,
              sortOrderCycle: ['asc', 'desc'],
            }}
            data={{
              columnDefs:
                props.vettingType === RFI_TYPE_SUPPLIER
                  ? supplierHeaderColumns
                  : locationHeaderColumns,
              rowData: vettingListData,
              defaultColDef: {
                cell: ({ row, column }) => {
                  const value = row.original?.[column.id]
                  if (
                    value &&
                    typeof value === 'object' &&
                    'cellDisplay' in value
                  ) {
                    return value.cellDisplay
                  }
                  return value ?? null
                },
              },
            }}
            pagination={{
              pageSize: queryParams.perPage,
              pageSizeOptions: pageSizeOptions,
              totalItems: totalCount,
              totalPages: Math.max(
                1,
                Math.ceil(totalCount / queryParams.perPage),
              ),
              currentPage: queryParams.pageNumber,
              itemRange: {
                start:
                  totalCount === 0
                    ? 0
                    : (queryParams.pageNumber - 1) * queryParams.perPage + 1,
                end: Math.min(
                  queryParams.pageNumber * queryParams.perPage,
                  totalCount,
                ),
              },
              hasTotalItems: true,
              hasPageSelector: true,
              hasPageSize: true,
              location: 'bottom',
              onRequestNext: () => {
                updateQueryParams({ pageNumber: queryParams.pageNumber + 1 })
              },
              onRequestPrev: () => {
                updateQueryParams({ pageNumber: queryParams.pageNumber - 1 })
              },
              onRequestLast: () => {
                const lastPage = Math.ceil(totalCount / queryParams.perPage)
                updateQueryParams({ pageNumber: lastPage })
              },
              onRequestFirst: () => {
                updateQueryParams({ pageNumber: 1 })
              },
              onPageSizeChange: (pageSize) => {
                const nextPageSize =
                  typeof pageSize === 'number'
                    ? pageSize
                    : pageSize?.newPageSize

                if (nextPageSize) {
                  updateQueryParams({
                    perPage: nextPageSize,
                    pageNumber: 1,
                  })
                }
              },
              onRequestPage: (page) => {
                const nextPage = typeof page === 'number' ? page : page?.newPage

                if (nextPage) {
                  updateQueryParams({ pageNumber: nextPage })
                }
              },
            }}
          />
        </Grid.Item>

        <TableFilterModal
          isOpen={isFilterOpen}
          closeModal={() => setIsFilterOpen(false)}
          vettingType={props.vettingType}
          allSelectedFilters={selectedFilters}
          handleFilterUpdate={handleFilterUpdate}
          handleFilterDataUpdate={setFilterData}
        />
      </Grid.Container>
    </>
  )
}

export default VettingSummaryTable
